<template>
  <!-- Travel Modal -->
  <div v-if="gameState.inTravelModal" class="travel-modal" @click="closeModal">
    <div class="travel-card" @click.stop>
      <!-- Fixed Header -->
      <div class="travel-header">
        <h3>选择目的地</h3>
        <p>选择你要前往的地点，每个地点都有不同的机遇和挑战。</p>
      </div>

      <!-- Scrollable Content -->
      <div class="travel-content">
        <div class="location-list">
        <button
          v-for="location in availableLocations"
          :key="location.name"
          @click="travelToLocation(location.name)"
          class="location-button"
          :class="{ 'current-location-btn': gameState.location === location.name }"
        >
            <div class="location-name">{{ location.name }}</div>
            <div class="location-description">{{ location.description }}</div>
            <div class="location-features">
              <div v-for="feature in location.features" :key="feature" class="location-feature">{{ feature }}</div>
            </div>
            <div v-if="gameState.location === location.name" class="current-location">当前位置</div>
          </button>
        </div>
      </div>

      <!-- Fixed Footer -->
      <div class="travel-footer">
        <button @click="handleCloseClick" class="close-btn">关闭</button>
      </div>
    </div>
  </div>
</template>

<script>
import gameController from '../game-controller/GameController.js';
import { availableLocations, LOCATION_NAMES } from '../exploration/exploration-data.js';

export default {
  name: 'TravelModal',
  props: {
    gameState: {
      type: Object,
      required: true
    }
  },
  computed: {
    availableLocations() {
      return availableLocations;
    }
  },
  methods: {
    travelToLocation(locationName) {
      // 如果已经在当前位置，不需要移动
      if (this.gameState.location === locationName) {
        this.$emit('travel-result', `你已经在 ${locationName} 了。`);
        this.$emit('close-modal');
        return;
      }

      // 处理特殊地点的移动逻辑
      if (locationName === LOCATION_NAMES.FORBIDDEN) {
        // 进入禁地时退出炼丹炼器界面
        this.gameState.inRefiningPill = false;
        this.gameState.inRefiningTreasure = false;
        this.gameState.refiningItemName = '';
        // 先设置位置为未知之地，表示在路上
        gameController.stateManager.setLocation(LOCATION_NAMES.UNKNOWN);
        gameController.forbiddenManager.generateMap();
        gameController.stateManager.enterForbiddenMap();
        this.$emit('travel-result', ['你在路上遭遇未知力量，被传送到禁地！', '地图已随机生成。', '可用命令：up, down, left, right, scan, exit, status, help']);
      } else if (locationName === LOCATION_NAMES.UNKNOWN) {
        // 直接前往未知之地
        gameController.stateManager.setLocation(LOCATION_NAMES.UNKNOWN);
        this.$emit('travel-result', `你进入了未知之地，周围的一切都显得那么神秘...`);
      } else {
        // 离开洞府时退出炼丹炼器界面
        if (this.gameState.location === LOCATION_NAMES.CAVE && locationName !== LOCATION_NAMES.CAVE) {
          this.gameState.inRefiningPill = false;
          this.gameState.inRefiningTreasure = false;
          this.gameState.refiningItemName = '';
        }
        // 先设置位置为未知之地，表示在路上
        gameController.stateManager.setLocation(LOCATION_NAMES.UNKNOWN);
        // 短暂延迟后到达目的地
        setTimeout(() => {
          gameController.stateManager.setLocation(locationName);
          this.$emit('travel-result', `经过一段旅程，你终于来到了 ${locationName}。`);
        }, 100);
      }

      // 保存状态
      gameController.stateManager.saveState();
      this.$emit('close-modal');
    },
    closeModal() {
      this.$emit('close-modal');
    },
    handleCloseClick() {
      this.closeModal();
    }
  }
};
</script>

<style scoped>
.travel-modal {
  position: absolute;
  inset: 0px 0px 0px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 6, 23, 0.8);
  z-index: 250;
  backdrop-filter: blur(2px);
}

.travel-card {
  background: #071328;
  border-radius: 12px;
  border: 1px solid #334155;
  color: #e6eef8;
  min-width: 400px;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.travel-header {
  flex-shrink: 0;
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #334155;
}

.travel-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.travel-footer {
  flex-shrink: 0;
  padding: 16px 20px 20px 20px;
  border-top: 1px solid #334155;
  display: flex;
  justify-content: center;
}

.travel-card h3 {
  margin: 0;
  font-size: 18px;
  color: #60a5fa;
  text-align: center;
  border-bottom: 1px solid #334155;
  padding-bottom: 8px;
}

.travel-card p {
  margin: 0;
  color: #cbd5e1;
  font-size: 14px;
  text-align: center;
  margin-top: 8px;
}

.location-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.location-button {
  padding: 16px;
  border: 2px solid #475569;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  border-radius: 8px;
  text-align: left;
  transition: all 0.3s ease;
  position: relative;
}

.location-button:hover:not(.current-location-btn) {
  border-color: #60a5fa;
  background: rgba(96, 165, 250, 0.1);
  transform: translateY(-2px);
}

.location-button.current-location-btn {
  border-color: #16a34a;
  background: rgba(22, 163, 74, 0.1);
}

.location-button.current-location-btn:hover {
  border-color: #16a34a;
  background: rgba(22, 163, 74, 0.2);
  transform: none;
}

.location-name {
  font-size: 16px;
  font-weight: bold;
  color: #dbeafe;
  margin-bottom: 4px;
}

.location-description {
  font-size: 14px;
  color: #94a3b8;
  margin-bottom: 8px;
}

.location-features {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.location-feature {
  background: #334155;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  color: #60a5fa;
}

.current-location {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #16a34a;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.close-btn {
  padding: 8px 16px;
  border: 1px solid #dc2626;
  background: transparent;
  color: #dc2626;
  cursor: pointer;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #dc2626;
  color: white;
}

/* 适配移动设备 */
@media (max-width: 900px) {
  .travel-card {
    min-width: 90vw;
  }

  .travel-header {
    padding: 16px 16px 12px 16px;
  }

  .travel-content {
    padding: 12px 16px;
  }

  .travel-footer {
    padding: 12px 16px 16px 16px;
  }

  .location-button {
    padding: 12px;
  }

  .location-name {
    font-size: 14px;
  }

  .location-description {
    font-size: 13px;
  }

  .location-features {
    gap: 4px;
  }

  .location-feature {
    font-size: 11px;
    padding: 1px 4px;
  }
}
</style>
